import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export default function SelectInput({
  selected = null,
  list,
  onChange,
  value,
  error,
  errorText,
  label,
  readOnly,
  isRequired,
  className,
  tabIndex,
}) {
  const wrapper = useRef(null);
  const [active, setActive] = useState(false);
  const [currentSelected, setCurrentSelected] = useState(selected);

  const onClick = (item) => {
    setCurrentSelected(item);
    if (onChange) onChange(item.id || item.value);
    setActive(false);
  };

  const toggleActive = () => {
    if (!readOnly) {
      setActive(!active);
    }
  };

  // Fix: Reset internal state when value prop changes to empty/null
  useEffect(() => {
    // If value is empty/null, reset currentSelected
    if (!value) {
      setCurrentSelected(null);
    } else {
      // If value is provided, find the matching item from list
      const matchedItem = list.find(
        (item) => item.id === value || item.value === value
      );
      setCurrentSelected(matchedItem || null);
    }
  }, [value, list]); // Add value and list as dependencies

  // Also sync with selected prop (for backward compatibility)
  useEffect(() => {
    setCurrentSelected(selected);
  }, [selected]);

  useEffect(() => {
    const windowClick = ({ target }) => {
      if (!wrapper.current.contains(target)) setActive(false);
    };

    if (active) window.addEventListener("click", windowClick);
    else window.removeEventListener("click", windowClick);

    return () => window.removeEventListener("click", windowClick);
  }, [active]);

  return (
    <div
      className={"select " + (active ? "active" : "")}
      ref={wrapper}
      style={{
        position: "relative",
        zIndex: active ? 9999 : 1, // Much higher z-index when active
      }}
    >
      {label && (
        <div className="select__label !mb-1">
          {label}
          {isRequired ? <span className="!text-red-500">*</span> : ""}
        </div>
      )}
      <div
        className={
          `select__selected ${className}` +
          (error && value === "" && !active ? " error" : "")
        }
        onClick={toggleActive}
        tabIndex={tabIndex}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleActive();
          }
        }}
        style={{
          position: "relative",
          zIndex: 1,
        }}
      >
        {currentSelected ? (
          currentSelected.value
        ) : (
          <span>-Select an option-</span>
        )}
        <ChevronDown color="#15803d" size={20} />
      </div>
      <ul
        className={"select__options " + (active ? "active" : "")}
        style={{
          position: "absolute",
          top: "calc(100% + 1px)", // 4px gap below the input
          left: 0,
          right: 0,
          maxHeight: active ? "200px" : "0",
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#e5e7eb transparent",
          zIndex: 9999,
          backgroundColor: "white",
          border: active ? "1px solid #e5e7eb" : "none",
          borderRadius: "0.375rem",
          boxShadow: active ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" : "none",
          transition: "max-height 0.2s ease-in-out",
        }}
      >
        {list && Array.isArray(list) && list.map((item, index) => (
          <li
            className="select__option"
            key={index}
            onClick={() => onClick(item)}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {item.value}
          </li>
        ))}
      </ul>
      {error && value === "" && !active && (
        <div className="select__error">{errorText}</div>
      )}
    </div>
  );
}
